// ============================================================================
// OPS HOME VIEW — Live Fires + Engine Dashboard
//
// Role access: intake_coordinator, triage_nurse, scheduler,
//              ops_manager, tenant_admin
//
// Data source: LIVE workflow/state reads (via listOpsActiveItems).
//   Stuck detection is derived from tenant Impact Settings SLA thresholds,
//   using business-hours-aware elapsed time for non-Scheduled statuses.
//   Scheduled stuck uses calendar duration: appointmentDate − scheduledAt.
//
// Firestore reads:
//   - tenants/{tenantId}/referrals (active items via listOpsActiveItems)
//   - tenants/{tenantId}/settings/impact (SLA + business hours config)
//
// Layout:
//   TOP ROW    = LIVE FIRES — referrals exceeding tenant SLA thresholds
//   BOTTOM ROW = THE ENGINE — live actionable queue counts
//
// Zero-PHI invariant: this view never reads from /phi/**.
// ============================================================================

import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../../auth/AuthContext.jsx';
import {
  listOpsActiveItems,
  getImpactSettings,
  DEFAULT_IMPACT_SETTINGS,
  WORKFLOW_STATUS,
  WORKFLOW_PRIORITY,
  isIntakeCoordinator,
  businessHoursElapsed,
  queueSlaStatus,
} from '../../core/index.js';
import {
  Activity, RefreshCw, AlertTriangle, Flame, Inbox,
  Clock, AlertCircle, ArrowRight, Zap, Calendar,
  CheckCircle, Eye,
} from 'lucide-react';

// ── Helpers ──────────────────────────────────────────────────────────────────

function toMillis(ts) {
  if (!ts) return null;
  if (ts.toMillis) return ts.toMillis();
  if (ts.seconds) return ts.seconds * 1000;
  if (ts instanceof Date) return ts.getTime();
  if (typeof ts === 'number') return ts;
  return null;
}

function isScheduledStuck(item, slaHours) {
  const apptStr = item.workflow?.scheduling?.appointmentDate;
  // Anchor on the actual scheduled time; fall back to status entry only if absent.
  const scheduledMs = toMillis(item.workflow?.scheduling?.scheduledAt)
                   || toMillis(item.workflow?.statusEnteredAt);
  if (!apptStr || !scheduledMs) return false;
  // Parse the calendar date deterministically (UTC), independent of browser timezone.
  const apptMs = new Date(apptStr + 'T00:00:00Z').getTime();
  if (Number.isNaN(apptMs)) return false;
  return (apptMs - scheduledMs) / (60 * 60 * 1000) >= slaHours;
}

function isStatusStuck(item, nowMs, slaHours, bh) {
  const enteredMs = toMillis(item.workflow?.statusEnteredAt)
                 || toMillis(item.workflow?.statusUpdatedAt)
                 || toMillis(item.workflow?.createdAt);
  if (!enteredMs) return false;
  const elapsed = businessHoursElapsed(enteredMs, nowMs, bh);
  return elapsed >= slaHours;
}

function isUrgent(item) {
  return item.workflow?.priority === WORKFLOW_PRIORITY.HIGH ||
         item.workflow?.priority === WORKFLOW_PRIORITY.URGENT ||
         item.workflow?.sourceOutcomeStatus === 'urgent';
}

function formatAge(ms) {
  if (ms <= 0) return 'just now';
  const hours = Math.floor(ms / (60 * 60 * 1000));
  if (hours < 1) return `${Math.floor(ms / (60 * 1000))}m`;
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  const remH = hours % 24;
  return remH > 0 ? `${days}d ${remH}h` : `${days}d`;
}

// ── Sub-components ──────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className="bg-slate-800/30 border border-slate-700/20 rounded-2xl p-5 animate-pulse">
      <div className="h-3 w-24 bg-slate-700/60 rounded mb-3" />
      <div className="h-10 w-16 bg-slate-700/60 rounded" />
    </div>
  );
}

const STATUS_STYLES = {
  ok:       { bg: 'bg-slate-800/30', border: 'border-slate-700/30', text: 'text-slate-500', pulse: '', badge: '' },
  warn:     { bg: 'bg-amber-500/10', border: 'border-amber-500/20', text: 'text-amber-400', pulse: 'bg-amber-500 animate-pulse', badge: 'bg-amber-500/20 text-amber-300' },
  critical: { bg: 'bg-red-500/10',   border: 'border-red-500/20',   text: 'text-red-400',   pulse: 'bg-red-500 animate-pulse', badge: 'bg-red-500/20 text-red-300' },
};

function LiveFireCard({ label, helperText, stuckCount, queueCount, warnThreshold, critThreshold, stuckPct, status, icon: Icon, onClick }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.ok;
  const isAlert = status !== 'ok';
  const Tag = onClick ? 'button' : 'div';

  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={`relative overflow-hidden rounded-2xl border p-5 transition-all text-left w-full
        ${s.bg} ${s.border} ${s.text}
        ${isAlert ? `shadow-lg ${onClick ? 'cursor-pointer hover:brightness-110' : ''}` : ''}`}
    >
      {isAlert && s.pulse && (
        <div className={`absolute inset-0 ${s.pulse} opacity-10 rounded-2xl`} />
      )}
      <div className="relative flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <p className={`text-xs font-medium mb-1 ${isAlert ? 'opacity-80' : 'text-slate-500'}`}>
            {label}
          </p>
          {queueCount === 0 ? (
            <p className="text-sm text-slate-500 mt-2">No active referrals in this queue</p>
          ) : (
            <>
              <p className={`text-4xl font-black tracking-tight ${isAlert ? '' : 'text-slate-600'}`}>
                {stuckCount}
              </p>
              <p className={`text-xs mt-1 ${isAlert ? 'opacity-70' : 'text-slate-600'}`}>
                {stuckPct}% of queue stuck
              </p>
              <p className={`text-[10px] mt-0.5 ${isAlert ? 'opacity-50' : 'text-slate-600'}`}>
                Warn: {warnThreshold}% · Critical: {critThreshold}%
              </p>
            </>
          )}
          <div className="flex items-center gap-2 mt-1.5">
            {status === 'critical' && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.badge}`}>
                <Flame className="w-3 h-3 inline mr-1" />Critical — {stuckPct}% stuck
              </span>
            )}
            {status === 'warn' && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.badge}`}>
                <AlertTriangle className="w-3 h-3 inline mr-1" />WARNING
              </span>
            )}
            {status === 'ok' && queueCount > 0 && (
              <span className="text-xs text-slate-500">Within threshold</span>
            )}
          </div>
          <p className={`text-[10px] mt-2 ${isAlert ? 'opacity-40' : 'text-slate-600'}`}>
            {helperText}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <Icon className={`w-6 h-6 ${isAlert ? 'opacity-40' : 'opacity-20'}`} />
          {isAlert && onClick && (
            <ArrowRight className="w-4 h-4 opacity-40" />
          )}
        </div>
      </div>
    </Tag>
  );
}

function EngineCard({ label, count, icon: Icon, colorTw, subtitle }) {
  return (
    <div className="bg-slate-800/50 border border-slate-700/40 rounded-2xl p-5 flex flex-col gap-1">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${colorTw}`} />
        <p className="text-xs font-medium text-slate-400">{label}</p>
      </div>
      <p className={`text-3xl font-bold ${colorTw}`}>
        {count}
      </p>
      {subtitle && (
        <p className="text-xs text-slate-500">{subtitle}</p>
      )}
    </div>
  );
}

// ── Main View ────────────────────────────────────────────────────────────────

export default function OpsHomeView({ onNavigate, activeFocusTarget }) {
  const { user } = useContext(AuthContext);
  const tenantId = user?.tenantId;
  const hideDownstream = isIntakeCoordinator(user?.role);

  const [items, setItems]       = useState(null);
  const [settings, setSettings] = useState(null);
  const [error, setError]       = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    if (!tenantId) return;
    try {
      const [data, impactSettings] = await Promise.all([
        listOpsActiveItems(tenantId),
        getImpactSettings(tenantId),
      ]);
      setItems(data);
      setSettings(impactSettings || DEFAULT_IMPACT_SETTINGS);
      setError(null);
    } catch (err) {
      console.error('[OpsHomeView] Failed to load ops items:', err);
      setError('Failed to load operational data. Please retry.');
    }
  };

  useEffect(() => { load(); }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = async () => {
    setRefreshing(true);
    setItems(null);
    await load();
    setRefreshing(false);
  };

  const loading = items === null && !error;

  // ── Derive dashboard from live data + tenant SLA settings ─────────────

  const dashboard = useMemo(() => {
    if (!items || !settings) return null;
    const nowMs = Date.now();

    const sla = settings.sla || DEFAULT_IMPACT_SETTINGS.sla;
    const states = sla.states || DEFAULT_IMPACT_SETTINGS.sla.states;
    const thresholds = sla.thresholds || DEFAULT_IMPACT_SETTINGS.sla.thresholds;
    const bh = settings.businessHours || DEFAULT_IMPACT_SETTINGS.businessHours;

    const warnPct = thresholds.warnPercent ?? 0.10;
    const critPct = thresholds.criticalPercent ?? 0.25;

    const unclaimedNewSla    = states.UNCLAIMED_NEW?.slaHours ?? 4;
    const needsInfoSla       = states.NEEDS_INFO?.slaHours ?? 48;
    const readyToScheduleSla = states.READY_TO_SCHEDULE?.slaHours ?? 24;
    const scheduledSla       = states.SCHEDULED?.slaHours ?? 168;
    const seenSla            = states.SEEN?.slaHours ?? 72;

    // Bucket items by status
    const byStatus = {
      [WORKFLOW_STATUS.NEW]:               [],
      [WORKFLOW_STATUS.NEEDS_INFO]:        [],
      [WORKFLOW_STATUS.READY_TO_SCHEDULE]: [],
      [WORKFLOW_STATUS.SCHEDULED]:         [],
      [WORKFLOW_STATUS.SEEN]:              [],
    };

    for (const item of items) {
      const status = item.workflow?.status;
      if (status && byStatus[status]) {
        byStatus[status].push(item);
      }
    }

    // UNCLAIMED_NEW is a derived subset: NEW + no assignedTo/ownerUid
    const unclaimedNew = byStatus[WORKFLOW_STATUS.NEW].filter(
      (it) => !it.workflow?.assignedTo && !it.workflow?.ownerUid
    );

    // Urgent items: NEW + high/urgent priority
    const urgentNew = byStatus[WORKFLOW_STATUS.NEW].filter(isUrgent);

    // ── Stuck detection per queue ──

    const stuckUnclaimedNew = unclaimedNew.filter(
      (it) => isStatusStuck(it, nowMs, unclaimedNewSla, bh)
    );
    const stuckNeedsInfo = byStatus[WORKFLOW_STATUS.NEEDS_INFO].filter(
      (it) => isStatusStuck(it, nowMs, needsInfoSla, bh)
    );
    // READY_TO_SCHEDULE stuck detection mirrors the Ops Manager dashboard:
    //   • triaged + not on-hold → scheduling wait, calendar clock from triageCompletedAt
    //     against the scheduling SLA (default 48h).
    //   • untriaged or on-hold → triage wait, business-hours clock from statusEnteredAt
    //     against readyToScheduleSla.
    const schedulingSlaMs = (sla.scheduling?.businessHours ?? 48) * 60 * 60 * 1000;
    const stuckReady = byStatus[WORKFLOW_STATUS.READY_TO_SCHEDULE].filter((it) => {
      const w = it.workflow;
      const triaged = !!(w?.triage?.triageCompletedAt || w?.triageCompletedAt);
      if (triaged && w?.onHold !== true) {
        const clockMs = toMillis(w?.triageCompletedAt) || toMillis(w?.triage?.triageCompletedAt);
        return clockMs && (nowMs - clockMs) >= schedulingSlaMs;
      }
      return isStatusStuck(it, nowMs, readyToScheduleSla, bh);
    });
    const stuckScheduled = byStatus[WORKFLOW_STATUS.SCHEDULED].filter(
      (it) => isScheduledStuck(it, scheduledSla)
    );
    const stuckSeen = byStatus[WORKFLOW_STATUS.SEEN].filter(
      (it) => isStatusStuck(it, nowMs, seenSla, bh)
    );

    function buildCard(stuckArr, queueArr) {
      const sc = stuckArr.length;
      const qc = queueArr.length;
      const ratio = qc === 0 ? 0 : sc / qc;
      return {
        stuckCount:    sc,
        queueCount:    qc,
        stuckPct:      qc === 0 ? 0 : Math.round(ratio * 100),
        warnThreshold: Math.round(warnPct * 100),
        critThreshold: Math.round(critPct * 100),
        status:        queueSlaStatus(sc, qc, warnPct, critPct),
      };
    }

    const cards = {
      unclaimedNew:    buildCard(stuckUnclaimedNew, unclaimedNew),
      needsInfo:       buildCard(stuckNeedsInfo, byStatus[WORKFLOW_STATUS.NEEDS_INFO]),
      readyToSchedule: buildCard(stuckReady, byStatus[WORKFLOW_STATUS.READY_TO_SCHEDULE]),
      scheduled:       buildCard(stuckScheduled, byStatus[WORKFLOW_STATUS.SCHEDULED]),
      seen:            buildCard(stuckSeen, byStatus[WORKFLOW_STATUS.SEEN]),
    };

    const totalFires = stuckUnclaimedNew.length + stuckNeedsInfo.length +
                       stuckReady.length + stuckScheduled.length + stuckSeen.length;

    return {
      cards,
      totalFires,

      // Engine counts
      unclaimedNew:    unclaimedNew.length,
      needsInfo:       byStatus[WORKFLOW_STATUS.NEEDS_INFO].length,
      readyToSchedule: byStatus[WORKFLOW_STATUS.READY_TO_SCHEDULE].length,
      urgentQueue:     urgentNew.length,
      totalActive:     items.length,
    };
  }, [items, settings]);

  // ── Render ──────────────────────────────────────────────────────────────

  const todayStr = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-8">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
            <Activity className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-white" data-learning-target="queue-health-title">Queue Health</h1>
            <p className="text-xs text-slate-400">
              Live — {todayStr}
              {dashboard && (
                <span className="ml-2 text-slate-500">
                  · {dashboard.totalActive} active referral{dashboard.totalActive !== 1 ? 's' : ''}
                </span>
              )}
            </p>
          </div>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing || loading}
          data-learning-target="queue-health-refresh-button"
          className={`flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-800/60 hover:bg-slate-700/60
                     text-slate-400 hover:text-white text-xs font-medium disabled:opacity-50 transition-all
                     ${activeFocusTarget === 'queue-health-refresh-button' ? 'ring-2 ring-cyan-400 animate-pulse' : ''}`}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* ── Error state ────────────────────────────────────────────────────── */}
      {error && (
        <div className="flex items-center gap-3 px-4 py-3 bg-red-500/10 border border-red-500/30
                        rounded-xl text-red-400 text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button
            onClick={handleRefresh}
            className="text-xs underline underline-offset-2 hover:text-red-300"
          >
            Retry
          </button>
        </div>
      )}

      {/* ── LIVE FIRES ─────────────────────────────────────────────────────── */}
      <section data-learning-target="queue-health-live-fires" className={activeFocusTarget === 'queue-health-live-fires' ? 'ring-2 ring-cyan-400 rounded-xl p-2 -m-2 animate-pulse' : ''}>
        <div className="flex items-center gap-2 mb-1">
          <Flame className="w-4 h-4 text-red-400" />
          <h2 className="text-sm font-semibold text-red-400 uppercase tracking-wider">
            Live Fires
          </h2>
          {dashboard && dashboard.totalFires > 0 && (
            <span className="ml-1 w-2 h-2 rounded-full bg-red-400 animate-pulse" />
          )}
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Referrals exceeding tenant SLA thresholds
        </p>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 5 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : dashboard && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div data-learning-target="queue-health-unclaimed-new-fire" className={activeFocusTarget === 'queue-health-unclaimed-new-fire' ? 'ring-2 ring-cyan-400 rounded-xl animate-pulse' : ''}>
              <LiveFireCard
                label="Unclaimed New"
                helperText="New referrals not yet claimed by intake."
                icon={AlertCircle}
                onClick={dashboard.cards.unclaimedNew.stuckCount > 0 && onNavigate ? () => onNavigate('workqueue') : undefined}
                {...dashboard.cards.unclaimedNew}
              />
            </div>
            <div data-learning-target="queue-health-needs-info-fire" className={activeFocusTarget === 'queue-health-needs-info-fire' ? 'ring-2 ring-cyan-400 rounded-xl animate-pulse' : ''}>
              <LiveFireCard
                label="Needs Info"
                helperText="Waiting for required information beyond SLA."
                icon={Clock}
                onClick={dashboard.cards.needsInfo.stuckCount > 0 && onNavigate ? () => onNavigate('not-yet-inbox') : undefined}
                {...dashboard.cards.needsInfo}
              />
            </div>
            {!hideDownstream && (
              <LiveFireCard
                label="Ready to Schedule"
                helperText="Complete referrals waiting too long to be scheduled."
                icon={Calendar}
                onClick={dashboard.cards.readyToSchedule.stuckCount > 0 && onNavigate ? () => onNavigate('scheduling') : undefined}
                {...dashboard.cards.readyToSchedule}
              />
            )}
            {!hideDownstream && (
              <LiveFireCard
                label="Scheduled"
                helperText="Appointments scheduled too far in the future."
                icon={Zap}
                onClick={dashboard.cards.scheduled.stuckCount > 0 && onNavigate ? () => onNavigate('scheduling') : undefined}
                {...dashboard.cards.scheduled}
              />
            )}
            {!hideDownstream && (
              <LiveFireCard
                label="Seen"
                helperText="Seen referrals waiting too long for closed loop."
                icon={Eye}
                {...dashboard.cards.seen}
              />
            )}
          </div>
        )}

        {dashboard && dashboard.totalFires === 0 && (
          <div className="mt-2 px-4 py-3 bg-emerald-500/5 border border-emerald-500/15
                          rounded-xl text-emerald-400 text-sm flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-400" />
            All items within SLA — no fires right now.
          </div>
        )}
      </section>

      {/* ── WORKLOAD BY STAGE ──────────────────────────────────────────────── */}
      <section
        data-learning-target="queue-health-workload-by-stage"
        className={activeFocusTarget === 'queue-health-workload-by-stage' || activeFocusTarget === 'queue-health-engine-volume' ? 'ring-2 ring-cyan-400 rounded-xl p-2 -m-2 animate-pulse' : ''}
      >
        <div data-learning-target="queue-health-engine-volume" className="flex items-center gap-2 mb-4">
          <Inbox className="w-4 h-4 text-blue-400" />
          <h2 className="text-sm font-semibold text-blue-400 uppercase tracking-wider">
            Workload by Stage
          </h2>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 gap-4">
            {Array.from({ length: 2 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : dashboard && (
          <div className="grid grid-cols-2 gap-4">
            <div data-learning-target="queue-health-unclaimed-new-volume" className={activeFocusTarget === 'queue-health-unclaimed-new-volume' ? 'ring-2 ring-cyan-400 rounded-xl animate-pulse' : ''}>
              <EngineCard
                label="Unclaimed New"
                count={dashboard.unclaimedNew}
                icon={Inbox}
                colorTw="text-blue-400"
                subtitle="Waiting for intake"
              />
            </div>
            <div data-learning-target="queue-health-needs-info-volume" className={activeFocusTarget === 'queue-health-needs-info-volume' ? 'ring-2 ring-cyan-400 rounded-xl animate-pulse' : ''}>
              <EngineCard
                label="Needs Info / Rework"
                count={dashboard.needsInfo}
                icon={Clock}
                colorTw="text-amber-400"
                subtitle="Awaiting GP response"
              />
            </div>
          </div>
        )}
      </section>

      {/* ── Footer note ────────────────────────────────────────────────────── */}
      <p className="text-xs text-slate-600 text-center">
        Live data from workflow states · SLA thresholds from tenant Impact Settings · Business-hours-aware elapsed time
      </p>
    </div>
  );
}
